import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import UserAvatar from "./UserAvatar";

/**
 * Animated welcome modal shown right after a successful sign-in / signup /
 * guest login. Auto-dismisses after ~2.4s. Pure presentation — owner mounts
 * it conditionally with `open` + `user`.
 */
export default function WelcomeModal({ open, user, onClose, autoCloseMs = 2400, message = "WELCOME BACK" }) {
  useEffect(() => {
    if (!open || !autoCloseMs) return;
    const t = setTimeout(() => { onClose?.(); }, autoCloseMs);
    return () => clearTimeout(t);
  }, [open, autoCloseMs, onClose]);

  return (
    <AnimatePresence>
      {open && user && (
        <motion.div
          key="welcome-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#02040D]/80 backdrop-blur-lg"
          onClick={onClose}
          data-testid="welcome-modal"
        >
          {/* Subtle grid background */}
          <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none" style={{
            backgroundImage: "linear-gradient(rgba(59,130,246,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.10) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          {/* Center glow */}
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.85, 0.5] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute pointer-events-none"
            style={{
              width: 520, height: 520,
              background: "radial-gradient(circle, rgba(59,130,246,0.50), transparent 60%)",
              filter: "blur(40px)",
            }}
          />
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -15 }}
            transition={{ type: "spring", stiffness: 240, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl px-8 py-10 sm:px-10 sm:py-12 text-center"
            style={{
              background: "linear-gradient(180deg, rgba(13,18,38,0.96) 0%, rgba(7,11,28,0.96) 100%)",
              border: "1px solid rgba(59,130,246,0.55)",
              boxShadow: "0 0 60px rgba(59,130,246,0.45), inset 0 0 30px rgba(59,130,246,0.10)",
            }}
          >
            {/* Top corner accents */}
            <span aria-hidden className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 border-blue-400/70 rounded-tl" />
            <span aria-hidden className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-blue-400/70 rounded-tr" />
            <span aria-hidden className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-blue-400/70 rounded-bl" />
            <span aria-hidden className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 border-blue-400/70 rounded-br" />

            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 12 }}
              className="mx-auto flex justify-center"
            >
              <UserAvatar src={user.avatar_url} size={88} glow />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mt-5 font-mono text-[11px] tracking-[0.45em] text-blue uppercase flex items-center justify-center gap-2"
            >
              <Sparkles size={12} /> {message} <Sparkles size={12} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className="font-heading font-black text-3xl sm:text-4xl mt-2 leading-tight"
              style={{ textShadow: "0 0 18px rgba(59,130,246,0.5)" }}
            >
              {user.username || "PLAYER"}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.42 }}
              className="mt-5 grid grid-cols-3 gap-2 text-xs font-mono"
            >
              <Stat label="ELO"    value={user.elo ?? 1000}    accent="text-blue" />
              <Stat label="RANK"   value={user.rank || "—"}     accent="text-purple" />
              <Stat label="WINS"   value={user.wins ?? 0}       accent="text-win" />
            </motion.div>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={onClose}
              className="mt-7 px-6 py-2.5 rounded-xl font-mono uppercase tracking-[0.3em] text-[11px] text-white"
              style={{
                background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                boxShadow: "0 0 22px rgba(59,130,246,0.5)",
              }}
              data-testid="welcome-modal-continue"
            >
              ENTER ARENA
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] py-2">
      <div className="text-[9px] text-faint tracking-[0.25em] uppercase">{label}</div>
      <div className={`font-heading font-black text-base ${accent}`}>{value}</div>
    </div>
  );
}
